/**
 * ===========================================================================
 *  WALLS — what makes a map obstruct
 * ===========================================================================
 *
 * Lembah Brantas obstructs nothing but the river: every march on it measured
 * exactly straight-line-minus-arrival-radius (DEV_PLAN, Step 7 note). A
 * fortress interior is the opposite — a set of courts, and the only ways
 * between them are gates. This module is the whole of that rule, derived once
 * from the active arena's `walls` record (`WALL_DEFS`, via layout.js) and
 * shared by everything that moves:
 *
 *   pushOut(x, z, r, out)     a body of radius r never stands inside a wall
 *   blocked(ax,az, bx,bz, p)  no line of sight / no straight march through one
 *   route(ax,az, gx,gz, buf)  the gates to walk through, as waypoints
 *   inside(x, z)              within the curtain (nothing spawns outside it)
 *   clear(x, z, r)            inside, and not touching any wall
 *   sweep(...)                how far a camera boom may extend before a wall
 *
 * The architecture is `bases.js`'s; the SHAPES it builds come from here
 * (`WALLS.runs`, `WALLS.gates`, `GATE_KINDS`), so the collider and the
 * masonry are one set of numbers and cannot drift apart.
 *
 * Everything is an oriented box: a wall run is one, each gate tower is one.
 * An arena with no walls has none, and every query returns at its first line
 * — Lembah Brantas pays one branch per call and behaves exactly as before.
 *
 * No allocation after import: queries write into caller-supplied objects and
 * module-scope scratch.
 */
import { WALL_DEFS } from './layout.js';

/**
 * Gate structures, in metres. `tw` is a tower's width ALONG the wall, `proj`
 * how far it stands proud of each wall face, `rise` its height as a multiple
 * of the wall's. A candi bentar is a split gate — two mirror-image towers
 * with the passage cut clean between them (Wringin Lawang); a paduraksa is a
 * roofed one, piers carrying a stepped crown over the passage (Bajang Ratu).
 */
export const GATE_KINDS = {
  bentar:    { tw: 5.4, proj: 1.5, rise: 1.85 },
  paduraksa: { tw: 4.6, proj: 1.9, rise: 2.35, pass: 0.38 },   // pass: clear height / tower height
  // a timber gatehouse: two log towers and a fighting bridge over the passage
  regol:     { tw: 3.8, proj: 1.3, rise: 1.95, pass: 0.60 },
};

/** Clearance a body keeps from a wall's centre line beyond its half-base:
 *  the footing is ~0.5 m wider than the battered core where it meets the
 *  ground (`bases.js wallRun`). */
const FOOT = 0.25;

/* ------------------------------------------------------------ derivation */

const runs = [];     // solid wall runs, for the builder
const gates = [];    // gate records, for the builder, the router and the HUD
const boxes = [];    // every collider: { cx, cz, ux, uz, hl, hw, top, kind }
/* Masonry that spans a passage — a paduraksa's lintel and crown. Nothing
 * walks into it (the passage is open beneath), so it is no collider; but a
 * camera boom threading the gate at eye height goes straight through it, and
 * the first capture put the camera outside the curtain, face to the kala. */
const spans = [];    // { cx, cz, ux, uz, hl, hw, lo, hi } — heights above ground
let bounds = null;   // [[x,z], ...] closed curtain polygon, or null
const closedPolys = [];  // every closed line, for enclosed()

function derive() {
  const lines = (WALL_DEFS && WALL_DEFS.lines) || [];
  for (const L of lines) {
    const pts = L.pts.slice();
    if (L.closed) pts.push(pts[0]);
    if (L.bounds) bounds = L.pts.slice();
    if (L.closed) closedPolys.push(L.pts.slice());
    const mat = L.material || 'brick';
    const lineGates = (L.gates || []).map((g) => ({ ...g, placed: false }));
    for (let k = 0; k + 1 < pts.length; k++) {
      const [x0, z0] = pts[k], [x1, z1] = pts[k + 1];
      const dx = x1 - x0, dz = z1 - z0, len = Math.hypot(dx, dz);
      const ux = dx / len, uz = dz / len, nx = -uz, nz = ux;
      // gates on this segment, by arc length
      const cuts = [];
      for (const g of lineGates) {
        const t = (g.at[0] - x0) * ux + (g.at[1] - z0) * uz;
        const off = Math.abs((g.at[0] - x0) * nx + (g.at[1] - z0) * nz);
        if (off < 0.75 && t > g.w * 0.5 && t < len - g.w * 0.5) {
          g.placed = true;
          cuts.push({ t, g });
        }
      }
      cuts.sort((a, b) => a.t - b.t);
      let s = 0, jam0 = false;
      const emit = (a, b, j0, j1) => {
        if (b - a < 0.2) return;
        runs.push({
          line: L.id, x0: x0 + ux * a, z0: z0 + uz * a, x1: x0 + ux * b, z1: z0 + uz * b,
          ux, uz, nx, nz, len: b - a, h: L.h, t0: L.t0, t1: L.t1, jam0: j0, jam1: j1, mat,
        });
      };
      for (const c of cuts) {
        emit(s, c.t - c.g.w * 0.5, jam0, true);
        const G = GATE_KINDS[c.g.kind];
        if (!G) throw new Error(`[walls] gate kind "${c.g.kind}" on "${L.id}" has no GATE_KINDS row`);
        gates.push({
          id: `${L.id}@${Math.round(c.g.at[0])},${Math.round(c.g.at[1])}`,
          line: L.id, name: c.g.name || null, kind: c.g.kind,
          x: x0 + ux * c.t, z: z0 + uz * c.t, ux, uz, nx, nz, w: c.g.w,
          h: L.h, t0: L.t0, t1: L.t1, tower: G, mat,
        });
        s = c.t + c.g.w * 0.5;
        jam0 = true;
      }
      emit(s, len, jam0, false);
    }
    // a gate that lands on no segment would be a hole in nobody's wall
    for (const g of lineGates) {
      if (!g.placed) throw new Error(`[walls] gate at ${g.at} does not lie on line "${L.id}"`);
    }
  }

  // colliders: each run, lengthened by its half-width at any end that is NOT
  // a gate jamb so corners and T-junctions close with no notch to slip into
  for (const r of runs) {
    const hw = r.t0 * 0.5 + FOOT;
    const e0 = r.jam0 ? 0 : hw, e1 = r.jam1 ? 0 : hw;
    const a = -e0, b = r.len + e1;
    const mid = (a + b) * 0.5;
    boxes.push({
      cx: r.x0 + r.ux * mid, cz: r.z0 + r.uz * mid, ux: r.ux, uz: r.uz,
      hl: (b - a) * 0.5, hw, h: r.h, kind: 0, jam0: r.jam0, jam1: r.jam1,
    });
  }
  // two towers per gate, their inner faces flush with the clear passage
  for (const g of gates) {
    const G = g.tower;
    const hw = g.t0 * 0.5 + G.proj;
    if (G.pass) {
      spans.push({ cx: g.x, cz: g.z, ux: g.ux, uz: g.uz, hl: g.w * 0.5, hw,
        lo: g.h * G.rise * G.pass, hi: g.h * G.rise });
    }
    for (const side of [-1, 1]) {
      const o = side * (g.w * 0.5 + G.tw * 0.5);
      boxes.push({
        cx: g.x + g.ux * o, cz: g.z + g.uz * o, ux: g.ux, uz: g.uz,
        hl: G.tw * 0.5, hw, h: g.h * G.rise, kind: 1,
      });
    }
  }
}
derive();

const NB = boxes.length;

/* ------------------------------------------------------------- the grid */

/* 16 m cells over the 520 m world. Every box is registered in every cell its
 * AABB touches after inflating by MAX_R, so a body of radius <= MAX_R standing
 * in a cell can only touch boxes listed in that cell. */
const HALF = 260, CELL = 16, GN = Math.ceil((HALF * 2) / CELL), MAX_R = 2.5;
const cellStart = new Int32Array(GN * GN + 1);
let cellItems = new Int16Array(0);
const bx0 = new Float32Array(NB), bx1 = new Float32Array(NB);
const bz0 = new Float32Array(NB), bz1 = new Float32Array(NB);
{
  for (let i = 0; i < NB; i++) {
    const b = boxes[i];
    const ex = Math.abs(b.ux) * b.hl + Math.abs(b.uz) * b.hw;
    const ez = Math.abs(b.uz) * b.hl + Math.abs(b.ux) * b.hw;
    bx0[i] = b.cx - ex; bx1[i] = b.cx + ex; bz0[i] = b.cz - ez; bz1[i] = b.cz + ez;
  }
  const lists = Array.from({ length: GN * GN }, () => []);
  const ci = (v) => Math.max(0, Math.min(GN - 1, Math.floor((v + HALF) / CELL)));
  for (let i = 0; i < NB; i++) {
    for (let j = ci(bz0[i] - MAX_R); j <= ci(bz1[i] + MAX_R); j++)
      for (let k = ci(bx0[i] - MAX_R); k <= ci(bx1[i] + MAX_R); k++) lists[j * GN + k].push(i);
  }
  let n = 0;
  for (let c = 0; c < GN * GN; c++) { cellStart[c] = n; n += lists[c].length; }
  cellStart[GN * GN] = n;
  cellItems = new Int16Array(n);
  for (let c = 0, p = 0; c < GN * GN; c++) for (const i of lists[c]) cellItems[p++] = i;
}

/* --------------------------------------------------------------- queries */

/**
 * Push a circle out of every wall it overlaps. Writes the corrected position
 * to `out.x/out.z` and the last contact normal to `out.nx/out.nz`; returns
 * true if it moved. Callers cancel the velocity component into `n`.
 */
export function pushOut(x, z, r, out) {
  out.x = x; out.z = z; out.nx = 0; out.nz = 0;
  if (NB === 0) return false;
  const gx = Math.floor((x + HALF) / CELL), gz = Math.floor((z + HALF) / CELL);
  if (gx < 0 || gz < 0 || gx >= GN || gz >= GN) return false;
  const c = gz * GN + gx;
  let hit = false;
  for (let p = cellStart[c], e = cellStart[c + 1]; p < e; p++) {
    const b = boxes[cellItems[p]];
    const dx = out.x - b.cx, dz = out.z - b.cz;
    const lx = dx * b.ux + dz * b.uz;             // along the wall
    const lz = dx * -b.uz + dz * b.ux;            // across it (normal = (-uz, ux))
    const qx = Math.abs(lx) - b.hl, qz = Math.abs(lz) - b.hw;
    if (qx > r || qz > r) continue;
    let px, pz;                                   // push, in box-local axes
    if (qx > 0 || qz > 0) {
      // outside the box: nearest point on it, pushed out along the offset
      const cx = Math.max(-b.hl, Math.min(b.hl, lx)), cz = Math.max(-b.hw, Math.min(b.hw, lz));
      const ox = lx - cx, oz = lz - cz;
      const d = Math.sqrt(ox * ox + oz * oz);
      if (d >= r) continue;
      if (d < 1e-6) continue;
      const k = (r - d) / d;
      px = ox * k; pz = oz * k;
    } else if (qx > qz) {
      // inside: out through whichever face is nearest
      px = (lx >= 0 ? 1 : -1) * (-qx + r); pz = 0;
    } else {
      px = 0; pz = (lz >= 0 ? 1 : -1) * (-qz + r);
    }
    const wx = px * b.ux - pz * b.uz, wz = px * b.uz + pz * b.ux;
    out.x += wx; out.z += wz;
    const wl = Math.sqrt(wx * wx + wz * wz) || 1;
    out.nx = wx / wl; out.nz = wz / wl;
    hit = true;
  }
  return hit;
}

/**
 * Entry parameter t in [0,1] at which segment a->b enters box i inflated by
 * `pad`, or -1 if it misses. Slab test in the box's own frame.
 */
function enter(i, ax, az, bx, bz, pad) { return enterBox(boxes[i], ax, az, bx, bz, pad); }
function enterBox(b, ax, az, bx, bz, pad) {
  const ox = ax - b.cx, oz = az - b.cz, dx = bx - ax, dz = bz - az;
  const la = ox * b.ux + oz * b.uz, lz = ox * -b.uz + oz * b.ux;
  const da = dx * b.ux + dz * b.uz, dn = dx * -b.uz + dz * b.ux;
  let t0 = 0, t1 = 1;
  const hl = b.hl + pad, hw = b.hw + pad;
  if (Math.abs(da) < 1e-9) { if (la < -hl || la > hl) return -1; }
  else {
    let u = (-hl - la) / da, v = (hl - la) / da;
    if (u > v) { const s = u; u = v; v = s; }
    if (u > t0) t0 = u; if (v < t1) t1 = v;
    if (t0 > t1) return -1;
  }
  if (Math.abs(dn) < 1e-9) { if (lz < -hw || lz > hw) return -1; }
  else {
    let u = (-hw - lz) / dn, v = (hw - lz) / dn;
    if (u > v) { const s = u; u = v; v = s; }
    if (u > t0) t0 = u; if (v < t1) t1 = v;
    if (t0 > t1) return -1;
  }
  return t0;
}

/** True if the straight segment a->b passes through any wall (inflated by `pad`). */
export function blocked(ax, az, bx, bz, pad = 0) {
  if (NB === 0) return false;
  const lx0 = Math.min(ax, bx) - pad, lx1 = Math.max(ax, bx) + pad;
  const lz0 = Math.min(az, bz) - pad, lz1 = Math.max(az, bz) + pad;
  for (let i = 0; i < NB; i++) {
    if (bx1[i] < lx0 || bx0[i] > lx1 || bz1[i] < lz0 || bz0[i] > lz1) continue;
    if (enter(i, ax, az, bx, bz, pad) >= 0) return true;
  }
  return false;
}

function inPoly(P, x, z) {
  let c = false;
  for (let i = 0, j = P.length - 1; i < P.length; j = i++) {
    const xi = P[i][0], zi = P[i][1], xj = P[j][0], zj = P[j][1];
    if ((zi > z) !== (zj > z) && x < ((xj - xi) * (z - zi)) / (zj - zi) + xi) c = !c;
  }
  return c;
}

/** Inside ANY closed wall line — a court, a terrace ring, a stockade. What
 *  the flora rule means by "inside the walls": on the kraton the curtain is
 *  one of these, and on a raid the stockade is, although it bounds nothing. */
export function enclosed(x, z) {
  for (let i = 0; i < closedPolys.length; i++) if (inPoly(closedPolys[i], x, z)) return true;
  return false;
}

/** Inside the curtain. True everywhere on an arena that has none. */
export function inside(x, z) {
  if (!bounds) return true;
  let c = false;
  for (let i = 0, j = bounds.length - 1; i < bounds.length; j = i++) {
    const xi = bounds[i][0], zi = bounds[i][1], xj = bounds[j][0], zj = bounds[j][1];
    if ((zi > z) !== (zj > z) && x < ((xj - xi) * (z - zi)) / (zj - zi) + xi) c = !c;
  }
  return c;
}

const _po = { x: 0, z: 0, nx: 0, nz: 0 };
/** Somewhere a body of radius r may be put down: inside, touching no wall. */
export function clear(x, z, r) {
  if (NB === 0) return true;
  if (!inside(x, z)) return false;
  return !pushOut(x, z, r, _po);
}

/**
 * Largest fraction of the segment focus->camera that stays `clearance` short
 * of any wall the boom would pass through BELOW that wall's top. `ground` is
 * the height the walls stand on (terrain.heightAt). Returns 1 when clear.
 */
export function sweep(fx, fy, fz, cx, cy, cz, clearance, ground) {
  if (NB === 0) return 1;
  const len = Math.sqrt((cx - fx) ** 2 + (cy - fy) ** 2 + (cz - fz) ** 2);
  if (len < 0.2) return 1;
  const lx0 = Math.min(fx, cx), lx1 = Math.max(fx, cx), lz0 = Math.min(fz, cz), lz1 = Math.max(fz, cz);
  let best = 1;
  for (let i = 0; i < NB; i++) {
    if (bx1[i] < lx0 || bx0[i] > lx1 || bz1[i] < lz0 || bz0[i] > lz1) continue;
    const t = enter(i, fx, fz, cx, cz, 0.35);
    if (t < 0) continue;
    const b = boxes[i];
    const y = fy + (cy - fy) * t;
    const top = (ground ? ground(b.cx, b.cz) : 0) + b.h + 2.4;   // merlons
    if (y > top) continue;
    const f = Math.max(0.12, (t * len - clearance) / len);
    if (f < best) best = f;
  }
  for (let i = 0; i < spans.length; i++) {
    const b = spans[i];
    const t = enterBox(b, fx, fz, cx, cz, 0.35);
    if (t < 0) continue;
    const y = fy + (cy - fy) * t, g0 = ground ? ground(b.cx, b.cz) : 0;
    if (y < g0 + b.lo - 0.4 || y > g0 + b.hi + 1.0) continue;   // under the lintel, or over the crown
    const f = Math.max(0.12, (t * len - clearance) / len);
    if (f < best) best = f;
  }
  return best;
}

/* --------------------------------------------------------------- routing */

/*
 * A visibility graph, built once. Nodes are a point in front of and behind
 * every gate (the pair joined through the passage) plus a point off every
 * box corner, so a route can round a free-standing obstacle like the Siti
 * Hinggil terrace. Nodes inside a wall or outside the curtain are dropped.
 * Two nodes are joined if a march between them clears every wall by PAD.
 */
const PAD = 1.4, GATE_OFF = 7.5, CORNER_OFF = 3.2;

/*
 * Routing-only plugs across every gate's passage. Without them a route could
 * reach the near node of a gate and then cut DIAGONALLY through the passage
 * to a goal that happened to be visible from there; a column that started
 * that diagonal a few metres early (every walker does — waypoints are taken
 * at an arrival radius) clipped the tower and pinned itself on the wall. With
 * the passage plugged for the router, the only way through a gate is its own
 * node pair, square to the wall. Line of sight and collision never see these.
 */
const plugs = gates.map((g) => ({
  cx: g.x, cz: g.z, ux: g.ux, uz: g.uz, hl: g.w * 0.5, hw: g.t0 * 0.5 + g.tower.proj,
}));
function inBox(b, x, z) {
  const dx = x - b.cx, dz = z - b.cz;
  return Math.abs(dx * b.ux + dz * b.uz) <= b.hl && Math.abs(dx * -b.uz + dz * b.ux) <= b.hw;
}
/** Does a march a->b pass through a gate's passage? A plug holding `a`
 *  itself is ignored: a body standing in a gate may leave it either way. */
function plugged(ax, az, bx, bz) {
  for (let i = 0; i < plugs.length; i++) {
    const p = plugs[i];
    if (inBox(p, ax, az)) continue;
    if (enterBox(p, ax, az, bx, bz, 0) >= 0) return true;
  }
  return false;
}
const routeBlocked = (ax, az, bx, bz, pad) => blocked(ax, az, bx, bz, pad) || plugged(ax, az, bx, bz);
const nodeX = [], nodeZ = [];
const gatePair = [];            // [a, b] node indices per gate
{
  if (NB > 0) {
    const ok = (x, z) => inside(x, z) && !pushOut(x, z, PAD, _po);
    for (const g of gates) {
      const hw = g.t0 * 0.5 + g.tower.proj + GATE_OFF;
      const ax = g.x - g.nx * hw, az = g.z - g.nz * hw, bxx = g.x + g.nx * hw, bzz = g.z + g.nz * hw;
      const a = nodeX.length; nodeX.push(ax); nodeZ.push(az);
      const b = nodeX.length; nodeX.push(bxx); nodeZ.push(bzz);
      gatePair.push([a, b]);
    }
    // Free ends only. A node off a gate JAMB sits in the mouth of the
    // passage, and a route through it cuts the gate diagonally: measured, 58
    // of 13,604 walks shaved a tower on the corner-cut and pinned themselves
    // on the wall beyond. A jamb already has the gate's own node pair, square
    // to the wall, which is the line a column actually marches through.
    for (const bx of boxes) {
      if (bx.kind !== 0) continue;
      for (const sx of [-1, 1]) for (const sz of [-1, 1]) {
        if (sx < 0 ? bx.jam0 : bx.jam1) continue;
        const lx = sx * (bx.hl + CORNER_OFF), lz = sz * (bx.hw + CORNER_OFF);
        const x = bx.cx + lx * bx.ux - lz * bx.uz, z = bx.cz + lx * bx.uz + lz * bx.ux;
        if (ok(x, z)) { nodeX.push(x); nodeZ.push(z); }
      }
    }
  }
}
const NN = nodeX.length;
const adj = new Float32Array(NN * NN).fill(-1);    // edge cost, -1 = none
{
  for (let i = 0; i < NN; i++) for (let j = i + 1; j < NN; j++) {
    if (routeBlocked(nodeX[i], nodeZ[i], nodeX[j], nodeZ[j], PAD)) continue;
    const d = Math.hypot(nodeX[j] - nodeX[i], nodeZ[j] - nodeZ[i]);
    adj[i * NN + j] = adj[j * NN + i] = d;
  }
  // through each gate, whatever the pad says about the towers either side
  for (const [a, b] of gatePair) {
    const d = Math.hypot(nodeX[b] - nodeX[a], nodeZ[b] - nodeZ[a]);
    adj[a * NN + b] = adj[b * NN + a] = d;
  }
}

export const routeInfo = { lastLen: 0 };

// Dijkstra scratch: two virtual nodes (start NN, goal NN+1) on top of the graph
const dist = new Float32Array(NN + 2), prev = new Int16Array(NN + 2), done = new Uint8Array(NN + 2);
const vs = new Float32Array(NN), vg = new Float32Array(NN);
const stackBuf = new Int16Array(NN + 2);

/**
 * Waypoints from a to g, written as x,z pairs into `buf` (at most `cap`
 * pairs). Returns how many were written: 0 means walk straight (nothing in
 * the way, or no way round was found — the caller walks at the goal and the
 * walls slide it, which is no worse than before this module existed).
 * The longest route on the kraton is 10 waypoints (four walls, two nodes per
 * gate); a caller whose path runs out short of the goal simply asks again
 * from where it stands. `lastLen` holds the untruncated length.
 */
export const ROUTE_CAP = 12;
export function route(ax, az, gx, gz, buf, off = 0, cap = ROUTE_CAP) {
  if (NB === 0 || NN === 0) return 0;
  // Straight when every wall is cleared by 0.5 m — even diagonally through a
  // gate. The plugs exist to stop a waypoint's early turn clipping a tower;
  // a straight march has no waypoint and so no early turn.
  if (!blocked(ax, az, gx, gz, 0.5)) return 0;
  const S = NN, G = NN + 1;
  for (let i = 0; i < NN; i++) {
    vs[i] = routeBlocked(ax, az, nodeX[i], nodeZ[i], 0.6) ? -1 : Math.hypot(nodeX[i] - ax, nodeZ[i] - az);
    vg[i] = routeBlocked(gx, gz, nodeX[i], nodeZ[i], 0.6) ? -1 : Math.hypot(nodeX[i] - gx, nodeZ[i] - gz);
  }
  dist.fill(1e9); prev.fill(-1); done.fill(0);
  dist[S] = 0;
  for (;;) {
    let u = -1, du = 1e9;
    for (let i = 0; i < NN + 2; i++) if (!done[i] && dist[i] < du) { du = dist[i]; u = i; }
    if (u < 0 || u === G) break;
    done[u] = 1;
    if (u === S) {
      for (let v = 0; v < NN; v++) if (vs[v] >= 0 && du + vs[v] < dist[v]) { dist[v] = du + vs[v]; prev[v] = S; }
      continue;
    }
    for (let v = 0; v < NN; v++) {
      const c = adj[u * NN + v];
      if (c >= 0 && du + c < dist[v]) { dist[v] = du + c; prev[v] = u; }
    }
    if (vg[u] >= 0 && du + vg[u] < dist[G]) { dist[G] = du + vg[u]; prev[G] = u; }
  }
  if (prev[G] < 0) { routeInfo.lastLen = 0; return 0; }
  let n = 0;
  for (let v = prev[G]; v !== S && v >= 0; v = prev[v]) stackBuf[n++] = v;
  routeInfo.lastLen = n;
  const m = Math.min(n, cap);
  for (let k = 0; k < m; k++) {
    const v = stackBuf[n - 1 - k];
    buf[off + k * 2] = nodeX[v]; buf[off + k * 2 + 1] = nodeZ[v];
  }
  return m;
}

/** Read-only view for the builder, the probes and the HUD. */
export const WALLS = {
  n: NB, runs, gates, boxes, spans, bounds, nodes: NN,
  pushOut, blocked, inside, enclosed, clear, sweep, route,
};
